import React from "react";
import PropTypes from "prop-types";
import momentPropTypes from "react-moment-proptypes";
import omit from "lodash/omit";

import "react-dates/lib/css/_datepicker.css";
import "react-dates/initialize";
import { Small } from "../Typography";

import { DateRangePicker, SingleDatePicker } from "react-dates";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import DateRangePickerShape from "react-dates/lib/shapes/DateRangePickerShape";
import { DateRangePickerPhrases } from "react-dates/lib/defaultPhrases";
import {
  START_DATE,
  END_DATE,
  HORIZONTAL_ORIENTATION,
  ANCHOR_LEFT,
  NAV_POSITION_TOP,
} from "react-dates/lib/constants";

const defaultDatePickerProps = {
  autoFocus: false,
  autoFocusEndDate: false,
  initialStartDate: null,
  initialEndDate: null,

  // input related props
  startDateId: START_DATE,
  startDatePlaceholderText: "Start Date",
  endDateId: END_DATE,
  endDatePlaceholderText: "End Date",
  disabled: false,
  required: false,
  screenReaderInputMessage: "",
  showClearDates: false,
  showDefaultInputIcon: false,
  customInputIcon: null,
  customArrowIcon: null,
  customCloseIcon: null,
  block: false,
  small: false,
  regular: false,
  invalid: false,

  // calendar presentation and interaction related props
  renderMonthText: null,
  orientation: HORIZONTAL_ORIENTATION,
  anchorDirection: ANCHOR_LEFT,
  horizontalMargin: 0,
  withPortal: false,
  withFullScreenPortal: false,
  initialVisibleMonth: null,
  numberOfMonths: 2,
  keepOpenOnDateSelect: false,
  reopenPickerOnClearDates: false,
  isRTL: false,

  // navigation related props
  navPosition: NAV_POSITION_TOP,
  navPrev: null,
  navNext: null,
  onPrevMonthClick() {},
  onNextMonthClick() {},
  onClose() {},

  // day presentation and interaction related props
  renderCalendarDay: undefined,
  renderDayContents: null,
  minimumNights: 1,
  enableOutsideDays: false,
  isDayBlocked: () => false,
  isOutsideRange: () => false,
  isDayHighlighted: () => false,
  displayFormat: "DD/MM/YYYY",
  monthFormat: "MMMM YYYY",
  phrases: DateRangePickerPhrases,

  stateDateWrapper: (date) => date,
};

const DatePickerLabel = styled.label`
  display: block;
  margin-bottom: ${themeGet("space.xs")};
  font-size: ${themeGet("fontSizes.1")};
  font-weight: ${(props) =>
    props.boldLabel ? themeGet("fontWeights.2") : themeGet("fontWeights.1")};
  color: ${(props) =>
    props.invalid
      ? themeGet("colors.dangerDark")
      : themeGet("colors.greyDarkest")};
`;

const DatePickerContainer = styled.div`
  .SingleDatePicker {
    width: ${(props) => (props.width ? props.width : "134px")};
  }
  .DateRangePicker {
    width: ${(props) => {
      if (props.width) {
        if (props.width.includes("px")) {
          const widthValue = parseInt(props.width, 10);
          return `${widthValue * 2}px`;
        }
        return props.width;
      }
      return "calc(134px * 2)";
    }};
  }
  .SingleDatePickerInput,
  .DateRangePickerInput {
    display: block;
    cursor: text;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    box-shadow: none;
    font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
    border: 0;
    border-radius: 0;
    background: transparent;
  }
  .SingleDatePickerInput__withBorder,
  .DateRangePickerInput__withBorder {
    border: none;
    border-radius: 0;
  }
  .DateInput {
    width: ${(props) => (props.width ? props.width : "134px")};
    background: none;
    position: relative;
  }
  .DateRangePickerInput_arrow_svg path {
    fill: ${(props) => themeGet("colors.greyLight")(props)};
  }
  .DateRangePickerInput_arrow {
    display: none;
  }
  .SingleDatePicker_picker,
  .DateRangePicker_picker {
    background: none;
    z-index: ${(props) => (props.zIndex ? props.zIndex : "1")};
  }

  .DateInput_input {
    display: block;
    cursor: text;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    box-shadow: none;
    font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
    font-size: ${(props) => themeGet("fontSizes.2")(props)};
    font-weight: ${(props) => themeGet("fontWeights.1")(props)};
    z-index: 1;
    transition: ${(props) => themeGet("transition.transitionDefault")(props)};
    background: ${(props) => themeGet("colors.white")(props)};
    color: ${(props) => themeGet("colors.greyDarkest")(props)};
    height: ${(props) => (props.height ? props.height : "40px")};
    width: ${(props) => (props.width ? props.width : "134px")};
    padding: 5px 12px 6px 12px;
    border-radius: ${(props) => themeGet("radii.2")(props)};
    border: 1px solid
      ${(props) =>
        props.invalid
          ? themeGet("colors.danger")(props)
          : themeGet("colors.black30")(props)};
    &:hover {
      border: 1px solid
        ${(props) =>
          props.invalid
            ? themeGet("colors.danger")(props)
            : themeGet("colors.primary")(props)};
    }
    &:focus {
      outline: 0;
      box-shadow: ${(props) => themeGet("shadows.thinOutline")(props)}
        ${(props) => themeGet("colors.primary30")(props)};
      border: 1px solid
        ${(props) =>
          props.invalid
            ? themeGet("colors.danger")(props)
            : themeGet("colors.primary")(props)};
    }
    &::placeholder {
      color: ${(props) => themeGet("colors.grey")(props)};
    }
  }
  .DateInput_input[disabled] {
    background: ${(props) => themeGet("colors.greyLightest")(props)};
    color: ${(props) => themeGet("colors.grey")(props)};
  }
  .DateInput_input__focused {
    outline: 0;
    box-shadow: ${(props) => themeGet("shadows.thinOutline")(props)}
      ${(props) => themeGet("colors.primary30")(props)};
    border: 1px solid ${(props) => themeGet("colors.primary")(props)};
  }
  .DateRangePickerInput .DateInput:first-child .DateInput_input {
    border-radius: ${(props) =>
      `${themeGet("radii.2")(props)} 0 0 ${themeGet("radii.2")(props)}`};
  }
  .DateRangePickerInput .DateInput:last-child .DateInput_input,
  .DateRangePickerInput__showClearDates .DateInput .DateInput_input,
  .DateRangePickerInput__showClearDates .DateInput .DateInput_input__disabled {
    border-radius: ${(props) =>
      `0 ${themeGet("radii.2")(props)} ${themeGet("radii.2")(props)} 0`};
  }

  .SingleDatePickerInput__showClearDate,
  .DateRangePickerInput__showClearDates {
    padding-right: 0;
    /* When empty, react-dates adds SingleDatePickerInput_clearDate__hide (visibility: hidden).
       Override to display: none so the button is removed from layout and does not reserve space,
       matching the original display:none / display:flex behavior and avoiding layout/Chromatic drift. */
    .SingleDatePickerInput_clearDate.SingleDatePickerInput_clearDate__hide,
    .DateRangePickerInput_clearDates.DateRangePickerInput_clearDates__hide {
      display: none;
    }
  }

  .SingleDatePickerInput__showClearDate,
  .DateRangePickerInput__showClearDates {
    display: flex;
    align-items: center;
  }

  .DateRangePickerInput__showClearDates
    .DateInput:first-child:has(.DateInput_input:not([value=""]))
    ~ .DateInput
    .DateInput_input,
  .DateRangePickerInput__showClearDates
    .DateInput:first-child
    ~ .DateInput
    .DateInput_input:not([value=""]) {
    border-radius: ${(props) =>
      `0 ${themeGet("radii.2")(props)} ${themeGet("radii.2")(props)} 0`};
  }
  .SingleDatePickerInput_clearDate,
  .DateRangePickerInput_clearDates {
    background-color: transparent;
    border-radius: ${(props) =>
      `0 ${themeGet("radii.2")(props)} ${themeGet("radii.2")(props)} 0`};
    height: ${(props) => (props.height ? props.height : "40px")};
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    position: absolute;
    top: auto;
    right: 0;
    transform: none;
    &:hover,
    &:focus {
      outline: 0;
      background: transparent;
      border-radius: ${(props) =>
        `0 ${themeGet("radii.2")(props)} ${themeGet("radii.2")(props)} 0`};
      svg path {
        fill: ${(props) =>
          props.invalid
            ? themeGet("colors.danger")(props)
            : themeGet("colors.primary")(props)};
      }
    }
  }
  & .DayPickerKeyboardShortcuts_panel {
    color: ${(props) => themeGet("colors.greyDarkest")(props)};
  }
  & .CalendarDay__default {
    transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  }
  & .CalendarDay__selected,
  & .CalendarDay__selected:active,
  & .CalendarDay__selected:hover {
    background: ${(props) => themeGet("colors.primary")(props)};
    border: 1px solid ${(props) => themeGet("colors.primary")(props)};
  }
  & .DayPickerKeyboardShortcuts_show__bottomRight {
    border-radius: 0 0 3px 0;
    overflow: hidden;
  }
  & .DayPickerKeyboardShortcuts_show__topRight {
    border-radius: 0 3px 0 0;
    overflow: hidden;
  }
  & .DayPickerKeyboardShortcuts_show__bottomRight::before,
  & .DayPickerKeyboardShortcuts_show__topRight::before {
    border-right: 33px solid ${(props) => themeGet("colors.primary")(props)};
    transition: ${(props) => themeGet("transition.transitionDefault")(props)};
  }
  & .DayPickerKeyboardShortcuts_show__bottomRight:hover::before,
  & .DayPickerKeyboardShortcuts_show__topRight:hover::before {
    border-right: 33px solid ${(props) => themeGet("colors.primaryDark")(props)};
  }

  & .CalendarDay__selected_span,
  & .CalendarDay__hovered_span,
  & .CalendarDay__hovered_span:active {
    background: ${(props) => themeGet("colors.primaryLight")(props)};
    border: 1px solid ${(props) => themeGet("colors.primaryLight")(props)};
    color: ${(props) => themeGet("colors.white")(props)};
  }
  /*  .CalendarDay__hovered_span, */
  & .CalendarDay__hovered_span:hover,
  & .CalendarDay__default:hover {
    background: ${(props) => themeGet("colors.primary")(props)};
    border: 1px double ${(props) => themeGet("colors.primary")(props)};
    color: ${(props) => themeGet("colors.white")(props)};
  }

  & .CalendarDay__selected_span:active,
  & .CalendarDay__selected_span:hover {
    background: ${(props) => themeGet("colors.primaryDarker")(props)};
    border: 1px solid ${(props) => themeGet("colors.primaryDarker")(props)};
  }
  .DateInput_fang {
    margin-top: 1px;
    z-index: ${(props) => (props.zIndex ? props.zIndex + 1 : "2")};
  }
`;

/**
 *
 * Datepicker uses airbnb's react datepicker under the hood. This is just a wrapper to override styles, the only props you need to specify is either single or range. For all functionality and additional required props refer to documentation here: <https://github.com/airbnb/react-dates>
 */

class DatePicker extends React.Component {
  render() {
    const mergedProps = { ...defaultDatePickerProps, ...this.props };
    const {
      single,
      range,
      width,
      height,
      zIndex,
      invalid,
      required,
      label,
      id,
      startDateId,
      boldLabel,
      ...pickerProps
    } = mergedProps;

    return (
      <DatePickerContainer
        width={width}
        height={height}
        invalid={invalid}
        zIndex={zIndex}
        required={required}
      >
        {single ? (
          <>
            {label && (
              <DatePickerLabel
                htmlFor={id}
                invalid={invalid}
                boldLabel={boldLabel}
              >
                {label}
                {required && (
                  <Small color="danger" ml="xs">
                    *
                  </Small>
                )}
              </DatePickerLabel>
            )}
            <SingleDatePicker id={id} {...pickerProps} />
          </>
        ) : range ? (
          <>
            {label && (
              <DatePickerLabel
                htmlFor={startDateId}
                invalid={invalid}
                boldLabel={boldLabel}
              >
                {label}
                {required && (
                  <Small color="danger" ml="xs">
                    *
                  </Small>
                )}
              </DatePickerLabel>
            )}
            <DateRangePicker id={id} {...pickerProps} />
          </>
        ) : null}
      </DatePickerContainer>
    );
  }
}

DatePicker.propTypes = {
  /** Specifies a single date picker */
  single: PropTypes.bool,
  /** Specifies a range date picker */
  range: PropTypes.bool,
  /** For Single DatePicker Date */
  date: momentPropTypes.momentObj,
  /** For Single DatePicker date change */
  onDateChange: PropTypes.func,
  /** For Single DatePicker input placeholder */
  placeholder: PropTypes.string,
  /** For Range DatePicker Start Date */
  startDate: momentPropTypes.momentObj,
  /** For Range DatePicker End Date */
  endDate: momentPropTypes.momentObj,
  /** For Range DatePicker dates change */
  onDatesChange: PropTypes.func,
  /** For focus state change */
  onFocusChange: PropTypes.func,
  /** Input focused state */
  focusedInput: PropTypes.bool,
  /** Date format */
  displayFormat: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /** For specifying width of input (specify units) */
  width: PropTypes.string,
  /** For specifying height of input (specify units) */
  height: PropTypes.string,
  /** Applies invalid input styles */
  invalid: PropTypes.bool,
  /**
   * Should not be visible
   * @ignore
   */
  autoFocus: PropTypes.bool,
  autoFocusEndDate: PropTypes.bool,
  stateDateWrapper: PropTypes.func,
  initialStartDate: momentPropTypes.momentObj,
  initialEndDate: momentPropTypes.momentObj,

  ...omit(DateRangePickerShape, [
    "startDate",
    "endDate",
    "onDatesChange",
    "focusedInput",
    "onFocusChange",
  ]),
};

/**
 * Test description
 */
export default DatePicker;
